import { useState, useEffect } from 'react'
import { Loader2, Database, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from '@/components/ui/sheet'
import type { AgentDetailResponse, AgentStatus, SqlSetup } from '@/types/api'

interface AgentDetailsSliderProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  agent: AgentDetailResponse | null
  onEdit: () => void
  onDelete: () => void
  onActivate: () => void
  onDeactivate: () => void
  onValidate: () => void
  onHealthCheck: () => void
  testConnection: (agentId: string) => Promise<{ connected: boolean }>
  introspectSchema?: (agentId: string) => Promise<Record<string, unknown>>
  enrichSchema?: (agentId: string) => Promise<Record<string, unknown>>
  onRefresh?: () => void
  fetchAgentInfo?: (baseUrl: string) => Promise<{ description?: string } | null>
}

const getStatusVariant = (status: AgentStatus): 'success' | 'warning' | 'destructive' | 'secondary' => {
  switch (status) {
    case 'active':
      return 'success'
    case 'pending_validation':
      return 'warning'
    case 'failed_validation':
      return 'destructive'
    case 'inactive':
    default:
      return 'secondary'
  }
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Never'
  try {
    return new Date(dateString).toLocaleString()
  } catch {
    return dateString
  }
}

const getConnectionBadge = (status: string | null) => {
  switch (status) {
    case 'connected':
      return <Badge variant="success">Connected</Badge>
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>
    default:
      return <Badge variant="secondary">Untested</Badge>
  }
}

export function AgentDetailsSlider({
  open,
  onOpenChange,
  agent,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onValidate,
  onHealthCheck,
  testConnection,
  introspectSchema,
  enrichSchema,
  onRefresh,
  fetchAgentInfo,
}: AgentDetailsSliderProps) {
  const [testingConnection, setTestingConnection] = useState(false)
  const [connectionTestResult, setConnectionTestResult] = useState<boolean | null>(null)
  const [introspecting, setIntrospecting] = useState(false)
  const [enriching, setEnriching] = useState(false)
  const [schemaActionMessage, setSchemaActionMessage] = useState<string | null>(null)
  const [fetchedDescription, setFetchedDescription] = useState<string | null>(null)
  const [fetchedCapabilities, setFetchedCapabilities] = useState<string[]>([])
  const [fetchedDataDomains, setFetchedDataDomains] = useState<string[]>([])

  // Fetch description, capabilities, data_domains from agent service when stored values are empty
  useEffect(() => {
    if (!agent) {
      setFetchedDescription(null)
      setFetchedCapabilities([])
      setFetchedDataDomains([])
      return
    }
    if (!agent.base_url) return
    let cancelled = false

    // Fetch description from /agent/info
    if (!agent.description && fetchAgentInfo) {
      fetchAgentInfo(agent.base_url).then((info) => {
        if (!cancelled && info?.description) setFetchedDescription(info.description)
      })
    }

    // Fetch capabilities/data_domains from /agent/manifest
    const needsCaps = !agent.capabilities?.length
    const needsDomains = !agent.data_domains?.length
    if (needsCaps || needsDomains) {
      fetch(`${agent.base_url.replace(/\/+$/, '')}/agent/manifest`, {
        signal: AbortSignal.timeout(5000),
      })
        .then(r => r.ok ? r.json() : null)
        .then(manifest => {
          if (cancelled || !manifest) return
          if (needsCaps && manifest.capabilities?.length) setFetchedCapabilities(manifest.capabilities)
          if (needsDomains && manifest.data_domains?.length) setFetchedDataDomains(manifest.data_domains)
        })
        .catch(() => {})
    }

    return () => { cancelled = true }
  }, [agent?.agent_id, agent?.base_url, fetchAgentInfo])

  if (!agent) return null

  const sqlSetup = agent.sql_setup as SqlSetup | null | undefined
  const isSql = agent.agent_type === 'sql'

  // Determine if edit should be blocked
  const editBlocked = isSql && sqlSetup
    ? sqlSetup.ai_descriptions_done < sqlSetup.schema_tables
    : false

  const handleTestConnection = async () => {
    setTestingConnection(true)
    setConnectionTestResult(null)
    try {
      const result = await testConnection(agent.agent_id)
      setConnectionTestResult(result.connected)
    } catch {
      setConnectionTestResult(false)
    } finally {
      setTestingConnection(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <div className="flex items-center gap-2">
            <SheetTitle>Agent Details</SheetTitle>
            <Badge variant={getStatusVariant(agent.status)}>{agent.status}</Badge>
          </div>
          <SheetDescription>{agent.description || fetchedDescription || 'No description provided'}</SheetDescription>
        </SheetHeader>
        <SheetClose onClose={() => onOpenChange(false)} />

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Agent ID</p>
                  <p className="font-mono text-sm">{agent.agent_id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Name</p>
                  <p>{agent.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Agent Type</p>
                  <Badge variant="outline">{agent.agent_type === 'sql' ? 'SQL' : 'Generic'}</Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Base URL</p>
                  <p className="font-mono text-xs break-all">{agent.base_url}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status & Health */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status & Health</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <Badge variant={getStatusVariant(agent.status)}>{agent.status}</Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Health Check Failures</p>
                  <p>{agent.health_check_failures}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Last Health Check</p>
                  <p className="text-sm">{formatDate(agent.last_health_check)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Created At</p>
                  <p className="text-sm">{formatDate(agent.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Updated At</p>
                  <p className="text-sm">{formatDate(agent.updated_at)}</p>
                </div>
                {agent.registered_by && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Registered By</p>
                    <p className="text-sm">{agent.registered_by}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Schema Status (SQL agents only) */}
          {isSql && sqlSetup && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Schema Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Connection Status</p>
                    {getConnectionBadge(sqlSetup.connection_status)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Tables Discovered</p>
                    <p className="text-lg font-semibold">{sqlSetup.schema_tables}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">AI Descriptions</p>
                    <p className="text-sm">
                      <span className="text-lg font-semibold">{sqlSetup.ai_descriptions_done}</span>
                      <span className="text-muted-foreground"> / {sqlSetup.schema_tables}</span>
                    </p>
                    {sqlSetup.schema_tables > 0 && (
                      <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                        <div
                          className="bg-primary rounded-full h-1.5 transition-all"
                          style={{
                            width: `${Math.round((sqlSetup.ai_descriptions_done / sqlSetup.schema_tables) * 100)}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Relationships</p>
                    <p className="text-lg font-semibold">{sqlSetup.schema_relationship_rows}</p>
                  </div>
                </div>

                {/* Test Connection Button */}
                <div className="flex items-center gap-3 pt-2 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestConnection}
                    disabled={testingConnection}
                    className="h-8"
                  >
                    {testingConnection ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <Database className="h-3.5 w-3.5 mr-1.5" />
                        Test Database Connection
                      </>
                    )}
                  </Button>
                  {connectionTestResult !== null && (
                    <div className="flex items-center gap-1.5 text-sm">
                      {connectionTestResult ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span className="text-green-600">Connection successful</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-destructive" />
                          <span className="text-destructive">Connection failed</span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Schema Action Buttons */}
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                  {introspectSchema && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={introspecting}
                      onClick={async () => {
                        setIntrospecting(true)
                        setSchemaActionMessage(null)
                        try {
                          await introspectSchema(agent.agent_id)
                          setSchemaActionMessage('Schema re-introspected successfully')
                          onRefresh?.()
                        } catch {
                          setSchemaActionMessage('Re-introspection failed')
                        } finally {
                          setIntrospecting(false)
                        }
                      }}
                      className="h-8"
                    >
                      {introspecting ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Re-introspecting...</>
                      ) : (
                        <><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Re-introspect Schema</>
                      )}
                    </Button>
                  )}
                  {enrichSchema && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={enriching}
                      onClick={async () => {
                        setEnriching(true)
                        setSchemaActionMessage(null)
                        try {
                          const result = await enrichSchema(agent.agent_id)
                          setSchemaActionMessage(
                            `AI descriptions generated for ${(result as Record<string, number>).tables_enriched} tables`
                          )
                          onRefresh?.()
                        } catch {
                          setSchemaActionMessage('AI enrichment failed')
                        } finally {
                          setEnriching(false)
                        }
                      }}
                      className="h-8"
                    >
                      {enriching ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Generating...</>
                      ) : (
                        <><Sparkles className="h-3.5 w-3.5 mr-1.5" />Generate AI Descriptions</>
                      )}
                    </Button>
                  )}
                </div>
                {schemaActionMessage && (
                  <p className="text-xs text-muted-foreground">{schemaActionMessage}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Validation Errors */}
          {agent.validation_errors && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-destructive">Validation Errors</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-3 rounded-md overflow-auto">
                  {JSON.stringify(agent.validation_errors, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Required Endpoints */}
          {agent.required_endpoints && agent.required_endpoints.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Required Endpoints</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside space-y-1">
                  {agent.required_endpoints.map((endpoint, idx) => (
                    <li key={idx} className="font-mono text-sm">
                      {endpoint}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Collection Names */}
          {agent.collection_names && agent.collection_names.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Collection Names</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {agent.collection_names.map((name, idx) => (
                    <Badge key={idx} variant="outline">
                      {name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Capabilities & Data Domains */}
          {(() => {
            const caps = agent.capabilities?.length ? agent.capabilities : fetchedCapabilities
            const domains = agent.data_domains?.length ? agent.data_domains : fetchedDataDomains
            return (caps.length > 0 || domains.length > 0) ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Capabilities & Data Domains</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {caps.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Capabilities</p>
                      <div className="flex flex-wrap gap-1">
                        {caps.map((cap: string) => (
                          <Badge key={cap} variant="outline" className="text-xs">{cap}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {domains.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Data Domains</p>
                      <div className="flex flex-wrap gap-1">
                        {domains.map((domain: string) => (
                          <Badge key={domain} variant="secondary" className="text-xs">{domain}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : null
          })()}

          {/* Manifest */}
          {agent.manifest && Object.keys(agent.manifest).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Manifest</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-3 rounded-md overflow-auto">
                  {JSON.stringify(agent.manifest, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex flex-wrap gap-2 px-6 py-4 border-t border-border">
          <div className="relative group">
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              disabled={editBlocked}
            >
              Edit
            </Button>
            {editBlocked && (
              <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-popover border border-border rounded text-xs text-muted-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-md">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                Edit disabled until all AI descriptions are completed
              </div>
            )}
          </div>
          {agent.status === 'active' ? (
            <Button variant="outline" size="sm" onClick={onDeactivate}>
              Deactivate
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={onActivate}>
              Activate
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onValidate}>
            Validate
          </Button>
          <Button variant="outline" size="sm" onClick={onHealthCheck}>
            Health Check
          </Button>
          <Button variant="destructive" size="sm" onClick={onDelete}>
            Delete
          </Button>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="ml-auto">
            Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
